import { useEffect, useMemo, useState } from "react";
import { Card, Col, Container, Row, Spinner, Alert, Button } from "react-bootstrap";
import { authFetch } from "@/lib/auth-fetch";
import { useUser } from "@/app/usercontext";
import { useNavigate } from "react-router-dom";

interface SessionTemplateDTO {
  id: number;
  dayOfWeek: string; // MONDAY..SUNDAY
  time: string; // HH:mm
  therapyType?: string;
  clientIds?: number[];
  clientNames?: string[];
  // Back-compat single client fields may still arrive from server
  clientId?: number;
  clientName?: string;
  therapistId?: number;
  therapistName?: string;
  helper1Id?: number;
  helper1Name?: string;
  helper2Id?: number;
  helper2Name?: string;
  horseId?: number;
  horseName?: string;
  notes?: string;
}

interface SessionDTO {
  id: number;
  therapyType: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:mm[:ss]
  therapistId?: number; // convenience duplicate of therapist
  therapist?: number;
}

const dayOrder: Record<string, number> = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
};

const dayLabels: Record<string, string> = {
  MONDAY: "Montag",
  TUESDAY: "Dienstag",
  WEDNESDAY: "Mittwoch",
  THURSDAY: "Donnerstag",
  FRIDAY: "Freitag",
  SATURDAY: "Samstag",
  SUNDAY: "Sonntag",
};


export function WeekPlanPage() {
  const user = useUser();
  const navigate = useNavigate();
  const [data, setData] = useState<SessionTemplateDTO[]>([]);
  const [sessions, setSessions] = useState<SessionDTO[]>([]);
  const [bookedKeys, setBookedKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    const weekStart = getWeekStart(new Date());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const isoDate = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    Promise.all([
      authFetch("/api/session-templates").then((res) => {
        if (!res.ok) throw new Error("Fehler beim Laden der Vorlagen");
        return res.json();
      }),
      authFetch(`/api/sessions?from=${isoDate(weekStart)}&to=${isoDate(weekEnd)}&size=500`).then((res) => {
        if (!res.ok) throw new Error("Fehler beim Laden der Sitzungen");
        return res.json();
      }),
    ])
      .then(([templates, sessionsPage]: [SessionTemplateDTO[], { items: SessionDTO[] }]) => {
        const sessionsData = sessionsPage?.items ?? [];
        if (!mounted) return;
        setData(templates || []);
        setSessions(sessionsData || []);

        // Build a quick lookup for current week's bookings by key: date|HH:mm|therapistId
        const start = getWeekStart(new Date());
        const end = new Date(start);
        end.setDate(end.getDate() + 6);

        const inThisWeek = (d: string) => {
          // d expected format YYYY-MM-DD
          const [y, m, dd] = d.split("-").map((x) => parseInt(x, 10));
          const dt = new Date(y, (m - 1), dd);
          dt.setHours(0, 0, 0, 0);
          const s = new Date(start); s.setHours(0,0,0,0);
          const e = new Date(end); e.setHours(23,59,59,999);
          return dt.getTime() >= s.getTime() && dt.getTime() <= e.getTime();
        };

        const keys = new Set<string>();
        for (const s of sessionsData || []) {
          if (!s.date || !s.time) continue;
          if (!inThisWeek(s.date)) continue;
          const therapistId = s.therapistId ?? s.therapist ?? 0;
          const timeKey = (s.time || '').slice(0,5);
          keys.add(`${s.date}|${timeKey}|${therapistId}`);
        }
        setBookedKeys(keys);
      })
      .catch((e) => setError(e.message || "Unbekannter Fehler"))
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const grouped = useMemo(() => {
    const g: Record<string, SessionTemplateDTO[]> = {};
    for (const t of data) {
      const key = t.dayOfWeek || "";
      if (!g[key]) g[key] = [];
      g[key].push(t);
    }
    for (const key of Object.keys(g)) {
      g[key].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    }
    return g;
  }, [data]);

  const sortedDays = useMemo(() => Object.keys(grouped).sort((a, b) => (dayOrder[a] || 99) - (dayOrder[b] || 99)), [grouped]);

  const onDelete = async (id: number) => {
    if (!window.confirm("Möchten Sie diese Vorlage wirklich löschen?")) return;
    try {
      const res = await authFetch(`/api/session-templates/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Fehler beim Löschen der Vorlage");
      setData((prev) => prev.filter((t) => t.id !== id));
    } catch (e: any) {
      alert(e.message || "Fehler beim Löschen");
    }
  };

  // Helper: start of current week (Monday)
  const getWeekStart = (ref: Date): Date => {
    const d = new Date(ref);
    d.setHours(0,0,0,0);
    // JS getDay(): 0=Sun ... 6=Sat -> convert to Monday=0
    const day = d.getDay();
    const diffToMonday = (day + 6) % 7; // Mon->0, Tue->1, ..., Sun->6
    d.setDate(d.getDate() - diffToMonday);
    return d;
  };

  const weekdayIndex = (dayName: string): number => {
    switch (dayName) {
      case 'MONDAY': return 0;
      case 'TUESDAY': return 1;
      case 'WEDNESDAY': return 2;
      case 'THURSDAY': return 3;
      case 'FRIDAY': return 4;
      case 'SATURDAY': return 5;
      case 'SUNDAY': return 6;
      default: return 0;
    }
  };

  const getDateForWeekday = (dayName: string): string => {
    const start = getWeekStart(new Date());
    const d = new Date(start);
    d.setDate(start.getDate() + weekdayIndex(dayName));
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const isTemplateBooked = (t: SessionTemplateDTO): boolean => {
    const dateStr = getDateForWeekday(t.dayOfWeek);
    const therapistId = t.therapistId ?? 0;
    const timeKey = (t.time || '').slice(0,5);
    return bookedKeys.has(`${dateStr}|${timeKey}|${therapistId}`);
  };

  return (
    <Container fluid>
      <Row className="mb-3 align-items-center">
        <Col>
          <h2 className="mb-0">Wochenplan</h2>
          {user?.role === "THERAPIST" && (
            <div className="text-muted">Nur eigene Vorlagen werden angezeigt.</div>
          )}
        </Col>
        {user?.role === "ADMIN" && (
          <Col xs="auto">
            <Button variant="primary" onClick={() => navigate("/admin/templates/new", { state: { from: "/week-plan" } })}>
              Neue Vorlage
            </Button>
          </Col>
        )}
      </Row>

      {loading && (
        <div className="d-flex justify-content-center p-5">
          <Spinner animation="border" />
        </div>
      )}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        <div className="week-plan-content">
          {sortedDays.length === 0 && (
            <Alert variant="info">Keine Vorlagen gefunden.</Alert>
          )}

          {sortedDays.map((day) => (
            <div key={day} className="mb-4">
              <h5 className="border-bottom pb-1 mb-2">{dayLabels[day] || day}</h5>

              {/* Card View */}
              <div>
                <Row xs={1} md={2} lg={3} xl={4} className="g-3">
                  {grouped[day].map((t) => (
                    <Col key={t.id}>
                      <Card className="h-100 shadow-sm">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="fw-bold">{t.time} <span className="text-primary ms-2">{t.therapyType}</span></div>
                            </div>
                            <div className="d-flex gap-1">
                            <Button
                                variant={isTemplateBooked(t) ? "success" : "warning"}
                                size="sm"
                                title={isTemplateBooked(t) ? "Zur Sitzung" : "Sitzung anlegen"}
                                onClick={() => {
                                  if (isTemplateBooked(t)) {
                                    navigate("/sessions", { state: { from: "/week-plan" } });
                                    return;
                                  }
                                  const dateStr = getDateForWeekday(t.dayOfWeek);
                                  const clients = (t.clientIds && t.clientIds.length > 0)
                                    ? t.clientIds.map((id) => String(id))
                                    : (t.clientId ? [String(t.clientId)] : [""]);
                                  const prefill = {
                                    date: dateStr,
                                    time: t.time || "",
                                    therapyType: t.therapyType || "",
                                    therapist: t.therapistId ? String(t.therapistId) : "",
                                    helper1: t.helper1Id ? String(t.helper1Id) : "",
                                    helper2: t.helper2Id ? String(t.helper2Id) : "",
                                    horse: t.horseId ? String(t.horseId) : "",
                                    clients,
                                    paidTherapist: t.paidTherapist !== undefined ? t.paidTherapist : true,
                                    notes: t.notes || "",
                                  } as const;
                                  navigate("/sessions/new", { state: { prefill, from: "/week-plan" } });
                                }}
                              >
                                {isTemplateBooked(t) ? "✓" : "+"}
                              </Button>
                              {user?.role === "ADMIN" && (
                                <>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    title="Vorlage bearbeiten"
                                    onClick={() => navigate(`/admin/templates/${t.id}/edit`, { state: { from: "/week-plan" } })}
                                  >
                                    ✎
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    title="Vorlage löschen"
                                    onClick={() => onDelete(t.id)}
                                  >
                                    <span className="icon-trash" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="mb-1">
                            <strong>Klienten:</strong> {(t.clientNames && t.clientNames.length > 0) ? t.clientNames.join(", ") : t.clientName || "-"}
                          </div>
                          <div className="mb-1">
                            <strong>Fachkraft:</strong> {t.therapistName}
                          </div>
                          {(t.helper1Name || t.helper2Name) && (
                            <div className="mb-1">
                              <strong>Helfer:</strong> {[t.helper1Name, t.helper2Name].filter(Boolean).join(", ")}
                            </div>
                          )}
                          <div className="mb-0">
                            <strong>Pferd:</strong> {t.horseName}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}
